<template>
  <div class="rangeGridSquareComponent" @click="e => $emit('click', e)">
    <div
      class="rangeGridSquareContainer"
      :class="{ dark: isDark, hasContent: hasContent }"
      :style="{ background: splitData.background, borderColor: splitData.borderColor }"
      @mousedown="mousedown"
      @mouseup="mouseup"
    >
      <div
        class="contentContainer"
        v-if="hasContent"
        :style="{ background: splitData.content.background }"
      >
        <div class="content">
          <div v-if="splitData.content.anyCardRules.length" class="anyCardRules rulesContainer">
            <div
              v-for="ruleData in splitData.content.anyCardRules"
              :key="ruleData.suit"
              class="anyCardRule rule"
            >
              <Suit :suit="ruleData.suit" :noColor="true" />
            </div>
          </div>
          <div v-if="splitData.content.highCardRules.length" class="highCardRules rulesContainer">
            <div
              v-for="ruleData in splitData.content.highCardRules"
              :key="ruleData.suit"
              class="highCardRule rule"
            >
              <div class="highCard">{{ highCard }}</div>
              <Suit :suit="ruleData.suit" :noColor="true" />
            </div>
          </div>
        </div>
      </div>

      <div class="text">{{ text }}</div>
      <div class="hoverTarget" @mouseenter="mouseenter"></div>
    </div>
  </div>
</template>

<script>
import { darken, isDark } from "../helpers/color";
import { squareComboStringsToSuitRules } from "../helpers/cards";
import Suit from "./Suit";

export default {
  name: "RangeGridSquare",
  components: { Suit },
  props: {
    squareString: { type: String, required: true },
    colorScheme: { type: Array, required: true },
    // Selected combos
    comboStringsByColorIndex: { type: Object, required: true },
    // Selected combos excluding board cards
    filteredComboStringsByColorIndex: { type: Object, required: true },
  },

  computed: {
    text() {
      return this.squareString.substr(0, 2);
    },
    isDark() {
      return this.filteredSplit.length > 0;
    },
    hasContent() {
      return this.filteredSplit.length >= 2;
    },
    highCard() {
      return this.squareString.substr(0, 1);
    },
    splitData() {
      if (this.split.length < 2) {
        const colorIndex = this.colorIndexForNonEmptyFilteredSubrange;
        const isColored = colorIndex >= 0;
        const style = isColored
          ? {
              background: this.colorScheme[colorIndex],
              borderColor: darken(this.colorScheme[colorIndex]),
            }
          : {};
        return style;
      }
      const sortedSplit = Object.entries(this.comboStringsByColorIndex)
        .map(([colorIndex, comboStrings]) => {
          const rules = squareComboStringsToSuitRules(this.squareString, comboStrings);
          return { colorIndex, comboStrings, rules };
        })
        // Use the subrange with less suit rules as the main content.
        // If a subrange has no rules, though, use the other one. Generally speaking, just because one
        // combo set can be reduced to rules, that doesn't mean its inverse can, too.
        .sort((a, b) => {
          if (a.rules.length !== b.rules.length) {
            if (a.rules.length === 0) return 1;
            if (b.rules.length === 0) return -1;
            return a.rules.length - b.rules.length;
          }
          return a.colorIndex - b.colorIndex;
        });
      const rules = sortedSplit[0].rules;
      const anyCardRules = rules.filter(ruleData => ruleData.rule === "any");
      const highCardRules = rules
        .filter(ruleData => ruleData.rule === "high")
        .map(ruleData => {
          return {
            suit: ruleData.suit,
            isExcluded: this.filteredComboStringsByColorIndex[sortedSplit[0].colorIndex].find(
              comboString => comboString.substr(0, 2) === `${this.highCard}${ruleData.suit}`
            ),
          };
        });
      const backgroundColor = this.colorScheme[sortedSplit[1].colorIndex];
      return {
        background: backgroundColor,
        borderColor: darken(backgroundColor),
        content: {
          background: this.colorScheme[sortedSplit[0].colorIndex],
          anyCardRules,
          highCardRules,
        },
      };
    },
    split() {
      return Object.values(this.comboStringsByColorIndex).filter(
        comboStrings => comboStrings.length > 0
      );
    },
    filteredSplit() {
      return Object.values(this.filteredComboStringsByColorIndex).filter(
        comboStrings => comboStrings.length > 0
      );
    },
    colorIndexForNonEmptyFilteredSubrange() {
      for (const [colorIndex, comboStrings] of Object.entries(
        this.filteredComboStringsByColorIndex
      )) {
        if (comboStrings.length > 0) return colorIndex;
      }
      return -1;
    },
  },

  methods: {
    mousedown(e) {
      if (e.buttons !== 1) return;
      this.$emit("startPaint", this.squareString);
    },
    mouseup(e) {
      if (e.buttons !== 1) return;
      this.$emit("endPaint", this.squareString);
    },
    mouseenter(e) {
      if (e.buttons !== 1) return;
      this.$emit("continuePaint", this.squareString);
    },
  },
};
</script>

<style lang="scss" scoped>
.rangeGridSquareComponent {
  width: 100%;
  height: 100%;
}

.rangeGridSquareContainer {
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid #999;
  display: flex;
  align-items: center;
  justify-content: center;

  .contentContainer {
    width: 85%;
    height: 85%;
    position: relative;
  }

  .content {
    position: absolute;
    top: 0;
    left: 0;
    font-size: 8px;
    color: white;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .rulesContainer {
    padding: 0 2px;
    display: flex;
    width: 100%;
    justify-content: space-between;
    .rule {
      display: flex;
      align-items: center;
      margin-right: 2px;
    }
    .highCard {
      font-size: 10px;
    }
  }

  .text {
    position: absolute;
    top: 0;
    left: -1px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    opacity: 0.15;
    font-size: 25px;
    letter-spacing: -2px;
  }
  &.dark .text {
    color: white;
    opacity: 0.6;
  }
  &.hasContent .text {
    opacity: 0.2;
  }

  .hoverTarget {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 100%;
  }
}
</style>
